import mixpanel from 'mixpanel-browser';
import { bool, func, shape, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { LayoutSingleColumn, NamedRedirect, Page } from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { isScrollingDisabled } from '../../../ducks/ui.duck';
import { mixPanelEventsConstants, mixPanelEventsScreens } from '../../../mixpanel';
import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { getMetadata } from '../../PageBuilder/PageBuilder';
import TopbarContainer from '../../TopbarContainer/TopbarContainer';
import CustomerAlgoliaPage from '../CustomerAlgoliaPage';
import FooterContainer from '../../FooterContainer/FooterContainer';
import CustomerHero from './CustomerHero/CustomerHero';
import css from './CustomerHomePage.module.css';
import DealTypes from './DealTypes/DealTypes';
import HowItWorks from './HowItWorks/HowItWorks';
import LatestServices from './LatestServices/LatestServices';
import MerchantCta from './MerchantCta/MerchantCta';
import NearbyMap from './NearbyMap/NearbyMap';
import PopularDeals from './PopularDeals/PopularDeals';
import TopCategories from './TopCategories/TopCategories';
import { onAddRewardPoints, onGetAcceptedAds, onUpdateAdClick } from '../../../util/api';
import Slider from 'react-slick';
import UserRewards from '../../../components/UserRewards/UserRewards';
import { trackEventsMixpanel } from '../../../mixpanel/mixpanel';
import { createResourceLocatorString } from '../../../util/routes';
import { getOnboardingRedirectTarget } from '../../../util/postAuthIntent';
import ApplePayButtonContainer from '../../../components/ApplePayCard/ApplePayCard';

/**
 * CustomerHomePage Component
 *
 * This component serves as the main landing page for customers.
 * It includes various sections such as a search hero, latest services,
 * popular deals, and more. It also manages search state and handles
 * location submissions.
 *
 * Props:
 * - intl: Internationalization object for translations.
 * - scrollingDisabled: Boolean to disable scrolling on the page.
 * - pageAssetsData: Metadata and other assets for the page.
 */
export const CustomerHomePage = props => {
  const { intl, pageAssetsData, organizedCategories, organizedDealCategories } = props;
  const scrollingDisabled = useSelector(isScrollingDisabled);
  // Extract metadata for the page
  const meta = pageAssetsData?.meta ? { ...pageAssetsData.meta } : {};
  const pageMetaProps = getMetadata(meta);

  // State to manage search input and location data
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState({});

  const [dealFilterParams, setDealFilterParams] = useState({});
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const currentUser = useSelector(state => state?.user?.currentUser);
  const { profile } = currentUser?.attributes || {};
  const { publicData } = profile || {};
  const [acceptedAds, setAcceptedAds] = useState([]);

  useEffect(() => {
    if (currentUser) {
      // trackEventsMixpanel(mixPanelEventsConstants.DASHBOARD, {
      //   action_type: mixPanelEventsConstants.VIEW_MAIN_DASHBOARD,
      //   screen: mixPanelEventsScreens.HOME,
      //   user_type: 'Customer',
      //   user_id: currentUser?.id.uuid,
      // });
      // trackEventsMixpanel(mixPanelEventsConstants.NAV_BAR, {
      //   action_type: mixPanelEventsConstants.GO_HOME,
      //   screen: mixPanelEventsScreens.HOME,
      //   user_type: 'Customer',
      //   user_id: currentUser?.id.uuid,
      // });
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchAcceptedAds = async () => {
      try {
        const response = await onGetAcceptedAds({
          channel: 'Web',
          placement: 'Homepage Top Banner',
        });
        const ads = Array.isArray(response) ? response : [];
        const shuffledAds = [...ads].sort(() => Math.random() - 0.5);
        const top5Ads = shuffledAds.slice(0, 5);
        setAcceptedAds(top5Ads);
      } catch (error) {
        console.error('Error fetching accepted ads:', error);
      }
    };
    fetchAcceptedAds();
  }, []);

  const onboardingRedirect =
    currentUser && getOnboardingRedirectTarget(currentUser, publicData?.userType || 'customer');

  if (onboardingRedirect) {
    return (
      <NamedRedirect
        name={onboardingRedirect.name}
        params={onboardingRedirect.params || {}}
      />
    );
  }

  /**
   * Handle the submission of the location search.
   * This function updates the location state with the submitted values.
   *
   * @param {Object} values - The submitted values from the location search.
   */
  const handleLocationSubmit = values => {
    setLocationQuery(values);
    // trackEventsMixpanel(mixPanelEventsConstants.DASHBOARD, {
    //   action_type: mixPanelEventsConstants.GLOBAL_SEARCH,
    //   screen: mixPanelEventsScreens.HOME,
    //   user_type: 'Customer',
    //   user_id: currentUser?.id.uuid,
    // });
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    adaptiveHeight: true,
  };

  const hasAcceptedAds = acceptedAds.length > 0;

  const handleAdClick = async adId => {
    try {
      await onUpdateAdClick({ adId });
      const ad = acceptedAds && acceptedAds.find(ad => ad.id === adId);
      if (ad?.listingtype === 'business-profile') {
        history.push(
          createResourceLocatorString('ViewBusinessPage', routeConfiguration, {
            id: ad?.listingid,
            listingType: ad?.listingtype,
          })
        );
      } else {
        history.push(
          createResourceLocatorString('ListingPage', routeConfiguration, {
            id: ad?.listingid,
            slug: '',
          })
        );
      }

      // Award points for the click if user is logged in
      if (currentUser?.id?.uuid) {
        try {
          const response = await onAddRewardPoints({
            userId: currentUser.id.uuid,
            adId,
            actionType: 'click',
          });

          // Don't show error for already claimed rewards
          if (!response.alreadyRewarded) {
            // Optionally show success message
            console.log('Points awarded successfully');
          }
        } catch (error) {
          console.error('Error awarding points:', error);
        }
      }

      if (ad?.url) {
        window.open(ad.url, '_blank');
      }
    } catch (error) {
      console.error('Error updating ad click:', error);
    }
  };

  const handleVideoView = async adId => {
    const ad = acceptedAds && acceptedAds.find(ad => ad.id === adId);
    if (ad?.listingtype === 'business-profile') {
      history.push(
        createResourceLocatorString('ViewBusinessPage', routeConfiguration, {
          id: ad?.listingid,
          listingType: ad?.listingtype,
        })
      );
    } else {
      history.push(
        createResourceLocatorString('ListingPage', routeConfiguration, {
          id: ad?.listingid,
          slug: '',
        })
      );
    }
    if (currentUser?.id?.uuid) {
      try {
        const response = await onAddRewardPoints({
          userId: currentUser.id.uuid,
          adId,
          actionType: 'video_view',
        });

        // Don't show error for already claimed rewards
        if (!response.alreadyRewarded) {
          // Optionally show success message
          console.log('Video view points awarded successfully');
        }
      } catch (error) {
        console.error('Error awarding video view points:', error);
      }
    }
  };
  return (
    <Page
      {...pageMetaProps}
      scrollingDisabled={scrollingDisabled}
      title={intl.formatMessage({ id: 'MerchantHero.manageBookingsHeading' })}
      referrer="origin"
    >
      <LayoutSingleColumn
        className={css.merchantMainWrapper}
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.mainWrapper}>
          <div className={css.innerBox}>
            <CustomerHero
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              history={history}
              routeConfiguration={routeConfiguration}
              config={config}
              handleLocationSubmit={handleLocationSubmit}
              locationQuery={locationQuery}
              intl={intl}
            />

            {hasAcceptedAds && (
              <div className={css.sliderItem}>
                <Slider {...settings}>
                  {acceptedAds.map((elm, index) => (
                    <div key={index} className={css.adSlide}>
                      {elm.media.includes('/video/') ? (
                        <video
                          src={elm.media}
                          alt={`Advertisement ${index + 1}`}
                          onClick={() => handleAdClick(elm.id)}
                          onPlay={() => handleVideoView(elm.id)}
                          style={{
                            cursor: 'pointer',
                            width: '100%',
                            height: '320px',
                            objectFit: 'cover',
                            objectPosition: 'center',
                          }}
                          autoPlay
                          muted
                          loop
                          playsInline
                        />
                      ) : (
                        <img
                          src={elm.media}
                          alt={`Advertisement ${index + 1}`}
                          onClick={() => handleAdClick(elm.id)}
                          style={{
                            cursor: 'pointer',
                            width: '100%',
                            height: '320px',
                            objectFit: 'cover',
                            objectPosition: 'center',
                          }}
                        />
                      )}
                    </div>
                  ))}
                </Slider>
              </div>
            )}

            <TopCategories
              intl={intl}
              history={history}
              routeConfiguration={routeConfiguration}
            />

            <NearbyMap intl={intl} history={history} routeConfiguration={routeConfiguration} />

            <HowItWorks />
          </div>

          {/* Full-bleed bands (Lovable HappeningNow / LastMinute) — outside padded .innerBox */}
          <div className={css.contentSections}>
            <PopularDeals
              dealFilterParams={dealFilterParams}
              setDealFilterParams={setDealFilterParams}
              organizedDealCategories={organizedDealCategories}
              intl={intl}
              history={history}
              routeConfiguration={routeConfiguration}
            />
            <LatestServices
              intl={intl}
              history={history}
              routeConfiguration={routeConfiguration}
            />
          </div>

          <div className={css.innerBox}>
            <MerchantCta />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

// Default props for the component
CustomerHomePage.defaultProps = {
  currentUser: null,
  verificationError: null,
};

// Prop types for validation
CustomerHomePage.propTypes = {
  currentUser: propTypes.currentUser,
  scrollingDisabled: bool.isRequired,
  submitVerification: func.isRequired,
  isVerified: bool,
  emailVerificationInProgress: bool.isRequired,
  verificationError: propTypes.error,

  // Router location object
  location: shape({
    search: string,
  }).isRequired,

  // Internationalization object
  intl: intlShape.isRequired,
};

export default injectIntl(CustomerHomePage);
